import React, { ReactElement } from "react"
import InlineCode from "../../components/inlineCode"
import Layout from "../../components/layout"
import { CodeExample } from "../../components/validatorFuncs"

const stopExample = `var validator = new ValidatorBuilder<Employee>()
    .Required(e => e.FirstName)
    .Email(e => e.Email)
    .Range(e => e.Salary, 50000, 100000)
    .LessThanOrEqual(e => e.Commenced, DateTime.Today) // this won't be executed
    .Build();
    
// validation will stop on first error
var result = validator.Validate(
    new Employee
    {
        FirstName = "Robbo",
        Email = "rob@bo.xyz",
        Salary = 40000,            // this is invalid
        Commenced = DateTime.Today // this is also invalid
    }, 
    new ValidationOptions 
    { 
        StopWhenInvalid = true 
    });

foreach (var rule in result.BrokenRules)
{
    Console.WriteLine($"[{rule.Rule}] {rule.Key}: {rule.Message}");
}

// output - only the first validation error
// [Range] Salary: Value must be in range '50000' to '100000'.`

const throwExample = `var validator = new ValidatorBuilder<Employee>()
    .Required(e => e.FirstName)
    .Email(e => e.Email)
    .Range(e => e.Salary, 50000, 100000)
    .LessThanOrEqual(e => e.Commenced, DateTime.Today, message: "Date cannot be in the future.")
    .Build();
    
// validation will throw an exception
try
{
    validator.Validate(
        new Employee
        {
            FirstName = "Robbo",
            Email = "rob@bo.xyz",
            Salary = 40000,            // this is invalid
            Commenced = DateTime.Today // this is also invalid
        }, 
        new ValidationOptions 
        { 
            ThrowWhenInvalid = true 
        });    
}
catch (ValidationException ex)
{
    // broken rules can be accessed in the exception
    foreach (var rule in ex.BrokenRules)
    {
        Console.WriteLine($"[{rule.Rule}] {rule.Key}: {rule.Message}");
    }
}

// output
// [Range] Salary: Value must be in range '50000' to '100000'.
// [LessThanOrEqual] Commenced: Date cannot be in the future.`

const addExample = `var validator = new ValidatorBuilder<string>()
    .With(ctx => 
    {
        throw new InvalidOperationException("Error");
    })
    .Build();
    
// the exception will be added to broken rules collection and not thrown
var result = validator.Validate("validate", 
    new ValidationOptions 
    { 
        AddBrokenRuleForException = true // this is the default
    });

foreach (var rule in result.BrokenRules)
{
    Console.WriteLine($"[{rule.Rule}] {rule.Key}: {rule.Message}");
}

// output
// [InvalidOperationException] String: Error`

function ValidationOptions(): ReactElement {
  return (
    <Layout>
      <h1>Validation Options</h1>
      <p>
        The <InlineCode code="ValidationOptions" /> class contains properties
        that controls the flow of execution when a validation rule is broken or
        an unhandled exception occurs.
      </p>
      <p>
        By default, Validatum will execute all validation functions in order and
        any exceptions will be added to the broken rules collection. Exceptions
        are not thrown unless explicitly set in{" "}
        <InlineCode code="ValidationOptions" />.
      </p>
      <hr className="my-6" />
      <h5 className="font-semibold text-sm mb-4">Properties</h5>
      <InlineCode
        className="text-sm text-blue-600 bg-blue-100 font-semibold"
        code="StopWhenInvalid"
      />
      <p className="mt-4">
        Indicates whether to stop validation when the first invalid rule occurs.
        Default is <strong>false</strong>.
      </p>
      <p>
        When set to true, execution of the validator will immediately end with
        the <InlineCode code="ValidationResult" /> containing a single item in
        the broken rules collection.
      </p>
      <h5 className="font-semibold text-sm my-4">Example Usage</h5>
      <CodeExample code={stopExample} />
      <hr className="my-6" />
      <InlineCode
        className="text-sm text-blue-600 bg-blue-100 font-semibold"
        code="ThrowWhenInvalid"
      />
      <p className="mt-4">
        Indicates whether to throw <InlineCode code="ValidationException" />{" "}
        when validation fails. Default is <strong>false</strong>.
      </p>
      <p>
        When set to true, the validator will execute all functions and only
        throw an exception if validation has failed.
      </p>
      <h5 className="font-semibold text-sm my-4">Example Usage</h5>
      <CodeExample code={throwExample} />
      <hr className="my-6" />
      <InlineCode
        className="text-sm text-blue-600 bg-blue-100 font-semibold"
        code="AddBrokenRuleForException"
      />
      <p className="mt-4">
        Indicates whether to add unhandled exceptions as broken rules when they
        occur. Default is <strong>true</strong>.
      </p>
      <p>
        When set to false, any unhandled exception will be thrown, regardless of
        the value of ThrowWhenInvalid.
      </p>
      <h5 className="font-semibold text-sm my-4">Example Usage</h5>
      <CodeExample code={addExample} />
    </Layout>
  )
}

export default ValidationOptions
